import { Apis } from "libs/apis";
import { useState, useEffect, useCallback, useMemo } from "react";

// Shared utility function
const getDateRange = (period: string) => {
  const today = new Date();
  const endDate = today.toISOString().split('T')[0];
  let startDate = new Date();

  switch (period) {
    case "thisWeek":
      startDate.setDate(today.getDate() - today.getDay());
      break;
    case "twoWeeks":
      startDate.setDate(today.getDate() - 14);
      break;
    case "thisMonth":
      startDate = new Date(today.getFullYear(), today.getMonth(), 1);
      break;
    case "thisYear":
      startDate = new Date(today.getFullYear(), 0, 1);
      break;
    default:
      startDate.setDate(today.getDate() - today.getDay());
  }

  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate
  };
};

export const useUserStats = () => {
  const [userStats, setUserStats] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUserStats = useCallback(async () => {
    try {
      const response = await Apis.getUserStats();
      setUserStats(response.data.data);
    } catch (error) {
      console.error("Error while fetching user stats", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUserStats();
  }, [fetchUserStats]);

  return useMemo(() => ({ userStats, isLoading }), [userStats, isLoading]);
};

export const useOrdersOverview = () => {
  const [ordersGraphStats, setOrdersStats] = useState<any>(null);
  const [ordersPeriod, setOrdersPeriod] = useState<string>("thisMonth");

  console.log(ordersGraphStats)

  const fetchOrderStats = useCallback(async () => {
    try {
      const { startDate, endDate } = getDateRange(ordersPeriod);
      const response = await Apis.getOrderGraphStats(startDate, endDate);
      setOrdersStats(response.data.data.orders);
    } catch (error) {
      console.error("Error while fetching order stats", error);
    }
  }, [ordersPeriod]);

  useEffect(() => {
    fetchOrderStats();
  }, [fetchOrderStats]);

  const setOrdersPeriodCallback = useCallback((period: string) => {
    setOrdersPeriod(period);
  }, []);

  return useMemo(() => ({
    ordersGraphStats,
    ordersPeriod,
    setOrdersPeriod: setOrdersPeriodCallback,
    orderRatio: ordersGraphStats?.ratio
  }), [ordersGraphStats, ordersPeriod, setOrdersPeriodCallback]);
};

export const useSessionsOverview = () => {
  const [sessionsGraphStats, setSessionsStats] = useState<any>(null);
  const [sessionsPeriod, setSessionsPeriod] = useState<string>("thisMonth");

  const fetchSessionStats = useCallback(async () => {
    try {
      const { startDate, endDate } = getDateRange(sessionsPeriod);
      const response = await Apis.getSessionGraphStats(startDate, endDate);
      setSessionsStats(response.data.data.sessions);
    } catch (error) {
      console.error("Error while fetching session stats", error);
    }
  }, [sessionsPeriod]);

  useEffect(() => {
    fetchSessionStats();
  }, [fetchSessionStats]);

  const setSessionsPeriodCallback = useCallback((period: string) => {
    setSessionsPeriod(period);
  }, []);

  return useMemo(() => ({
    sessionsGraphStats,
    sessionsPeriod,
    setSessionsPeriod: setSessionsPeriodCallback,
    sessionRatio: sessionsGraphStats?.ratio
  }), [sessionsGraphStats, sessionsPeriod, setSessionsPeriodCallback]);
};

export const useSalesGraph = () => {
  const [salesGraphStats, setSalesGraphStats] = useState<any>(null);
  const [salesPeriod, setSalesPeriod] = useState<string>("thisMonth");
  const [overviewText, setOverviewText] = useState<string>("Overview of this week");

  const fetchSalesStats = useCallback(async () => {
    try {
      const response = await Apis.getSalesGraphStats(salesPeriod);
      setSalesGraphStats(response.data.data);
    } catch (error) {
      console.error("Error while fetching sales stats", error);
    }
  }, [salesPeriod]);

  useEffect(() => {
    fetchSalesStats();
  }, [fetchSalesStats]);

  const setSalesPeriodCallback = useCallback((period: string) => {
    setSalesPeriod(period);
    switch (period) {
      case "thisWeek":
        setOverviewText("Overview of this week");
        break;
      case "twoWeeks":
        setOverviewText("Overview of two weeks");
        break;
      case "thisMonth":
        setOverviewText("Overview of this month");
        break;
      case "thisYear":
        setOverviewText("Overview of this year");
        break;
      default:
        setOverviewText("Overview of this week");
    }
  }, []);

  return useMemo(() => ({
    salesGraphStats,
    salesPeriod,
    setSalesPeriod: setSalesPeriodCallback,
    overviewText
  }), [salesGraphStats, salesPeriod, setSalesPeriodCallback, overviewText]);
};

// Memoize the hook exports
const useUserStatsMemo = useUserStats;
const useOrdersOverviewMemo = useOrdersOverview;
const useSessionsOverviewMemo = useSessionsOverview;
const useSalesGraphMemo = useSalesGraph;

export const useDashboard = () => {
  return {
    useUserStats: useUserStatsMemo,
    useOrdersOverview: useOrdersOverviewMemo,
    useSessionsOverview: useSessionsOverviewMemo,
    useSalesGraph: useSalesGraphMemo
  };
};
